<template>
  <b-card no-body class="metabase-dashboard" :class="cssVarClass">
    <portal to="header-actions">
      <b-button variant="outline-primary" size="sm" :disabled="loading" @click="fetchIframeUrl">
        <feather-icon icon="RefreshCwIcon" class="mr-50" />
        {{ t('Refresh') }}
      </b-button>
    </portal>

    <b-card-body class="p-0">
      <!-- Loading -->
      <div
        v-if="loading || (iframeUrl && iframeLoading)"
        class="metabase-dashboard__placeholder d-flex align-items-center justify-content-center"
      >
        <div class="text-center">
          <b-spinner variant="primary" class="mb-1" />
          <p class="text-muted mb-0">{{ t('Loading report...') }}</p>
        </div>
      </div>

      <!-- Error -->
      <div
        v-else-if="error"
        class="metabase-dashboard__placeholder d-flex align-items-center justify-content-center"
      >
        <div class="text-center">
          <feather-icon icon="AlertCircleIcon" size="48" class="text-danger mb-1" />
          <p class="text-danger mb-1">{{ t('Failed to load report') }}</p>
          <p class="text-muted small mb-2">{{ error }}</p>
          <b-button variant="primary" size="sm" @click="fetchIframeUrl">
            {{ t('Try Again') }}
          </b-button>
        </div>
      </div>

      <!-- Iframe -->
      <iframe
        v-if="iframeUrl"
        v-show="!loading && !iframeLoading"
        class="metabase-dashboard__iframe"
        :src="iframeUrl"
        frameborder="0"
        allowtransparency="true"
        :title="title || 'Metabase Dashboard'"
        @load="onIframeLoad"
      />
    </b-card-body>
  </b-card>
</template>

<script>
import { BCard, BCardBody, BButton, BSpinner } from 'bootstrap-vue';
import { useUtils as useI18nUtils } from '@core/libs/i18n';
import { helper } from '@core/mixins/helper.js';
import useService from '@/auth/service/useService';

export default {
  name: 'MetabaseDashboard',

  components: {
    BCard,
    BCardBody,
    BButton,
    BSpinner
  },

  mixins: [helper],

  props: {
    /** Metabase dashboard ID — zorunlu */
    dashboardId: {
      type: Number,
      required: true
    },
    /** Kart başlığı */
    title: {
      type: String,
      default: ''
    },
    /** Minimum iframe yüksekliği (px) */
    minHeight: {
      type: Number,
      default: 600
    }
  },

  data() {
    return {
      iframeUrl: null,
      loading: false,
      iframeLoading: false,
      error: null,
      iframeHeight: this.minHeight,
      resizeObserver: null,
      uid: Math.random().toString(36).slice(2, 8)
    };
  },

  computed: {
    cssVarClass() {
      return `metabase-dashboard--${this.uid}`;
    }
  },

  watch: {
    dashboardId() {
      this.fetchIframeUrl();
    },
    iframeHeight(val) {
      this.setCssVars(val);
    },
    minHeight(val) {
      this.setCssVars(this.iframeHeight || val);
    }
  },

  async created() {
    await this.fetchIframeUrl();
  },

  mounted() {
    this.setCssVars(this.iframeHeight);
    this.initResizeObserver();
  },

  beforeDestroy() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  },

  methods: {
    async fetchIframeUrl() {
      this.loading = true;
      this.iframeLoading = true;
      this.error = null;
      this.iframeUrl = null;

      try {
        const response = await useService.getMetabaseIframeUrl(this.dashboardId);
        const url = response?.data?.iframeUrl;

        if (!url) throw new Error(this.$t('Invalid response from server'));

        this.iframeUrl = url;
      } catch (err) {
        const message =
          err?.message || err?.detail || err?.errors || this.$t('An unexpected error occurred');
        this.error = typeof message === 'string' ? message : JSON.stringify(message);
        this.showErrorMessage(this.$t('Failed to load report'));
      } finally {
        this.loading = false;
      }
    },

    onIframeLoad() {
      this.iframeLoading = false;
    },

    setCssVars(height) {
      if (!this.$el) return;
      this.$el.style.setProperty('--mb-placeholder-min-height', `${this.minHeight}px`);
      this.$el.style.setProperty('--mb-iframe-height', `${height}px`);
    },

    initResizeObserver() {
      if (typeof ResizeObserver === 'undefined') return;
      const el = this.$el;
      if (!el) return;

      const recalc = () => {
        const windowHeight = window.innerHeight;
        const top = el.getBoundingClientRect().top;
        this.iframeHeight = Math.max(this.minHeight, windowHeight - top - 80);
      };

      this.resizeObserver = new ResizeObserver(recalc);
      this.resizeObserver.observe(el);
      recalc();
    }
  },

  setup() {
    const { t } = useI18nUtils();
    return { t };
  }
};
</script>

<style scoped>
.metabase-dashboard {
  --mb-placeholder-min-height: 600px;
  --mb-iframe-height: 600px;
}

.metabase-dashboard__placeholder {
  min-height: var(--mb-placeholder-min-height);
  background-color: inherit;
}

.metabase-dashboard__iframe {
  display: block;
  width: 100%;
  height: var(--mb-iframe-height);
}
</style>
